/**
 * 配置值读取器的核心实现类.
 *
 * 负责从不同来源（URL参数、全局变量）读取配置值，
 * 并处理配置项之间的依赖关系和值转换。
 *
 * @packageDocumentation
 * @author yusangeng@outlook.com
 */
import { __spreadArray } from "tslib";
import isUndefined from "lodash/isUndefined";
import isFunction from "lodash/isFunction";
import noop from "lodash/noop";
import split from "./split";
var isArray = Array.isArray;
var keys = Object.keys, defineProperty = Object.defineProperty;
/**
 * 配置值读取器.
 *
 * @class Reader
 * @private
 */
var Reader = /** @class */ (function () {
    /**
     * 构造函数.
     *
     * @param {KonphGlobal} globalConf 全局配置变量.
     * @param {string} url url字符串.
     * @param {KonphOptions} options 配置项读取设置.
     *
     * @memberof Reader
     */
    function Reader(globalConf, url, options) {
        var _this = this;
        this.globalConf_ = globalConf;
        this.urlConf_ = split(url);
        this.options_ = options;
        var cache = (this.cache_ = {});
        var fitContext = (this.fitContext_ = {});
        keys(options).forEach(function (key) {
            var kk = key.toLowerCase().trim();
            cache[kk] = noop;
            defineProperty(fitContext, kk, {
                get: function () { return _this.item(kk); }
            });
        });
    }
    /**
     * 读取配置项.
     *
     * @param {string} key 配置项名称(不区分大小写)，两边的空格会被trim掉.
     * @returns {any} 配置项的值.
     *
     * @memberof Reader
     * @instance
     */
    Reader.prototype.item = function (key) {
        var _this = this;
        var _a;
        var kk = (typeof key === "string" ? key.toLowerCase().trim() : key);
        var cacheValue = (_a = this.cache_[kk]) !== null && _a !== void 0 ? _a : noop;
        if (cacheValue !== noop) {
            // 命中缓存
            return cacheValue;
        }
        // 当前项的配置
        var item = this.options_[kk];
        if (typeof item === "undefined" || item === null) {
            throw new Error("Bad key: ".concat(String(kk), "."));
        }
        if (["string", "number", "boolean"].includes(typeof item)) {
            return item;
        }
        if (item.__konph_private_item__) {
            // 私有配置
            return item.value;
        }
        var _b = item, def = _b.def, defaultValue = _b.defaultValue, fit = _b.fit, deps = _b.deps;
        // url参数的优先级最高
        var value = this.urlConf_[kk];
        if (isUndefined(value)) {
            // 全局变量优先级次之
            value = this.globalConf_[kk];
        }
        if (isUndefined(value)) {
            // 默认值优先级最低
            value = isUndefined(def) ? defaultValue : def; // defaultValue是def的老写法
        }
        if (isFunction(fit)) {
            // 由于url参数总是字符串, 所以如果必要的话, 这里需要使用一个fit函数转型
            // 如果需要对配置值做一些特殊处理, 也可在fit执行
            // 兼容老版本
            // 老版本不能自动分析依赖, 需要使用deps字段声明依赖
            var depValues = [];
            if (isArray(deps) && deps.length) {
                // 要计算当前配置项的值，首先读取它依赖的配置项
                depValues = deps.map(function (dep) { return _this.item(dep); });
            }
            if (depValues.length) {
                // 老写法
                // 如果填写了deps, 同时fit函数是箭头函数, 则依赖分析不可用
                value = fit.call.apply(fit, __spreadArray([this.fitContext_, value], depValues, false));
            }
            else {
                // 新写法
                // 由于fit函数有通常是箭头函数, bind this无效，所以要使用第二个参数传递fitContext
                value = fit.call(this.fitContext_, value, this.fitContext_);
            }
        }
        this.cache_[kk] = value;
        return value;
    };
    return Reader;
}());
export default Reader;
//# sourceMappingURL=data:application/json;base64,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